<template>
  <teleport to="body">
    <transition name="ios-glass-fade">
      <div v-if="visible" class="ios-glass-overlay" @click.self="handleOverlayDismiss">
        <div class="ios-glass-modal" :style="modalStyle">
          <div class="ios-glass-blur"></div>
          <div class="ios-glass-card">
            <div v-if="title" class="ios-glass-title">{{ title }}</div>
            <div v-if="description || $slots.description" class="ios-glass-desc-wrap">
              <slot name="description">
                <div v-if="description" class="ios-glass-desc">{{ description }}</div>
              </slot>
            </div>
            <slot />
            <div v-if="showActions" class="ios-glass-actions" :class="{ single: !showSecondary }">
              <button
                v-if="showSecondary"
                class="ios-glass-btn secondary"
                :class="{ 'secondary-success': secondaryHighlighted }"
                :disabled="primaryLoading"
                @click="handleSecondaryClick"
              >
                {{ secondaryText }}
              </button>
              <button
                class="ios-glass-btn primary"
                :disabled="primaryDisabled || primaryLoading"
                @click="$emit('confirm')"
              >
                {{ primaryLoading ? loadingText : primaryText }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  visible: { type: Boolean, default: false },
  title: { type: String, default: '' },
  description: { type: String, default: '' },
  showActions: { type: Boolean, default: true },
  showSecondary: { type: Boolean, default: true },
  primaryText: { type: String, default: '确定' },
  secondaryText: { type: String, default: '取消' },
  loadingText: { type: String, default: '处理中...' },
  primaryLoading: { type: Boolean, default: false },
  primaryDisabled: { type: Boolean, default: false },
  /** 点击左侧次要按钮时是否自动关闭弹窗 */
  closeOnSecondary: { type: Boolean, default: true },
  /** 左侧次要按钮是否显示成功态（如复制成功） */
  secondaryHighlighted: { type: Boolean, default: false },
  /** 弹窗最大宽度，默认与上传诊断日志弹窗一致 */
  maxWidth: { type: String, default: 'min(320px, calc(100vw - 36px))' },
});

const modalStyle = computed(() => ({
  width: props.maxWidth,
}));

const emit = defineEmits(['update:visible', 'confirm', 'cancel']);

function handleSecondaryClick() {
  if (props.closeOnSecondary) {
    emit('update:visible', false);
  }
  emit('cancel');
}

function handleOverlayDismiss() {
  emit('update:visible', false);
}
</script>

<style scoped>
.ios-glass-fade-enter-active,
.ios-glass-fade-leave-active {
  transition: opacity 0.24s ease;
}

.ios-glass-fade-enter-from,
.ios-glass-fade-leave-to {
  opacity: 0;
}

.ios-glass-overlay {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.35);
  z-index: 15000;
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(10px);
}

.ios-glass-modal {
  position: relative;
  border-radius: 35px;
  overflow: hidden;
  isolation: isolate;
  background-color: rgba(0, 0, 0, 0.29);
}

.ios-glass-blur {
  position: absolute;
  inset: -40px;
  background: rgba(245, 245, 245, 0.6);
  filter: blur(20px);
  backdrop-filter: blur(40px);
  border-radius: 34px;
  z-index: 0;
}

.ios-glass-card {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: rgba(255, 255, 255, 0.54);
  border-radius: 20px;
  padding: 22px 16px 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
}

.ios-glass-title {
  font-size: 17px;
  font-weight: 700;
  color: #000;
  text-align: center;
  letter-spacing: -0.2px;
}

.ios-glass-desc-wrap {
  width: 100%;
}

.ios-glass-desc {
  font-size: 14px;
  line-height: 1.5;
  color: #3c3c43;
  text-align: center;
  padding: 0 6px;
}

.ios-glass-actions {
  display: flex;
  gap: 10px;
  margin-top: 4px;
}

.ios-glass-actions.single {
  justify-content: center;
}

.ios-glass-btn {
  flex: 1;
  height: 42px;
  border-radius: 99px;
  border: none;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.ios-glass-actions.single .ios-glass-btn.primary {
  flex: 0 0 85%;
  max-width: 85%;
}

.ios-glass-btn.secondary {
  background: rgba(118, 118, 128, 0.12);
  color: #000;
}

.ios-glass-btn.secondary.secondary-success {
  background: rgba(19, 115, 51, 0.16);
  color: #137333;
  box-shadow: inset 0 0 0 1px rgba(19, 115, 51, 0.28);
}

.ios-glass-btn.primary {
  background: #007aff;
  color: #fff;
}

.ios-glass-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
</style>
